import React, { useState, useEffect, useContext } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Plus, Edit2, Trash2, Search, Loader, AlertCircle, X, Percent } from "lucide-react";
import { NotificationContext } from "../../context/NotificationContext";
import authAxios from "../../api/authAxios";

export default function TaxManagement() {
  const { addNotification } = useContext(NotificationContext);
  const [taxes, setTaxes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedTax, setSelectedTax] = useState(null);

  const [formData, setFormData] = useState({
    name: '',
    gst_percentage: '',
    is_active: true
  });

  useEffect(() => {
    fetchTaxes();
  }, []);

  const fetchTaxes = async () => {
    try {
      setLoading(true);
      const response = await authAxios.get('/api/super-admin/taxes/');
      setTaxes(response.data);
    } catch (error) {
      addNotification("Failed to load taxes", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = (tax = null) => {
    if (tax) {
      setSelectedTax(tax);
      setFormData({
        name: tax.name,
        gst_percentage: tax.gst_percentage,
        is_active: tax.is_active
      });
    } else {
      setSelectedTax(null);
      setFormData({
        name: '',
        gst_percentage: '',
        is_active: true
      });
    }
    setIsModalOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...formData,
        name: `GST ${formData.gst_percentage}%`,
        gst_percentage: parseFloat(formData.gst_percentage) || 0,
      };

      if (selectedTax) {
        await authAxios.patch(`/api/super-admin/taxes/${selectedTax.id}/`, payload);
        addNotification("Tax rate updated successfully", "success");
      } else {
        await authAxios.post('/api/super-admin/taxes/', payload);
        addNotification("Tax rate created successfully", "success");
      }
      setIsModalOpen(false);
      fetchTaxes();
    } catch (error) {
      addNotification(error.response?.data?.error || "Error saving tax rate", "error");
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this tax rate?')) {
      try {
        await authAxios.delete(`/api/super-admin/taxes/${id}/`);
        addNotification("Tax rate deleted successfully", "success");
        fetchTaxes();
      } catch (error) {
        addNotification("Error deleting tax rate", "error");
      }
    }
  };

  const filteredTaxes = taxes.filter(tax => 
    tax.name.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="space-y-6 max-w-7xl mx-auto pb-10">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-8">
        <div>
          <h1 className="text-3xl font-black text-slate-900 dark:text-white tracking-tight flex items-center gap-3">
            <Percent className="w-8 h-8 text-blue-600 dark:text-blue-400" />
            Tax Rates Management
          </h1>
          <p className="text-slate-500 dark:text-slate-400 font-medium mt-1">Configure global tax presets available for products</p>
        </div>
        
        <button
          onClick={() => handleOpenModal()}
          className="flex items-center gap-2 px-6 py-3 bg-slate-900 dark:bg-white text-white dark:text-slate-900 rounded-xl font-bold hover:bg-slate-800 dark:hover:bg-slate-100 transition-all shadow-lg active:scale-95"
        >
          <Plus className="w-5 h-5" />
          Add Tax Rate
        </button>
      </div>

      {/* Search */}
      <div className="relative mb-6">
        <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 w-5 h-5" />
        <input
          type="text"
          placeholder="Search tax rates by name..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="w-full pl-12 pr-4 py-4 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 font-medium shadow-sm transition-all"
        />
      </div>

      {/* List */}
      {loading ? (
        <div className="flex justify-center items-center py-20">
          <Loader className="w-8 h-8 animate-spin text-blue-500" />
        </div>
      ) : filteredTaxes.length === 0 ? (
        <div className="bg-white dark:bg-slate-800 rounded-xl p-10 text-center border border-slate-200 dark:border-slate-700 shadow-sm">
          <AlertCircle className="w-12 h-12 text-slate-400 mx-auto mb-4 opacity-50" />
          <h3 className="text-xl font-bold text-slate-900 dark:text-white mb-2">No Tax Rates Found</h3>
          <p className="text-slate-500 dark:text-slate-400">Add some tax rates to get started.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          <AnimatePresence>
            {filteredTaxes.map((tax) => (
              <motion.div
                key={tax.id}
                layout
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.9 }}
                className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-2xl p-6 shadow-sm hover:shadow-md transition-all group relative overflow-hidden"
              >
                <div className="absolute -right-4 -top-4 w-24 h-24 bg-blue-50 dark:bg-blue-900/20 rounded-full blur-2xl opacity-50 pointer-events-none"></div>
                <div className="flex justify-between items-start mb-4 relative z-10">
                  <div>
                    <h3 className="text-lg font-bold text-slate-900 dark:text-white">{tax.name}</h3>
                  </div>
                  <div className="flex items-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                    <button
                      onClick={() => handleOpenModal(tax)}
                      className="p-2 text-slate-400 hover:text-blue-500 hover:bg-blue-50 dark:hover:bg-slate-700 rounded-lg transition"
                    >
                      <Edit2 className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleDelete(tax.id)}
                      className="p-2 text-slate-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-slate-700 rounded-lg transition"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-3 mt-4">
                  <div className="bg-slate-50 dark:bg-slate-900/50 p-3 rounded-xl border border-slate-100 dark:border-slate-700/50">
                    <p className="text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1">Total GST</p>
                    <p className="text-lg font-black text-slate-900 dark:text-white">{tax.gst_percentage}%</p>
                  </div>
                  <div className="bg-blue-50/50 dark:bg-blue-900/10 p-3 rounded-xl border border-blue-100/50 dark:border-blue-800/30">
                    <p className="text-[10px] font-bold text-blue-500 uppercase tracking-wider mb-1">Split (C+S)</p>
                    <p className="text-sm font-bold text-slate-700 dark:text-slate-300">
                      {tax.cgst_percentage}% + {tax.sgst_percentage}%
                    </p>
                  </div>
                </div>

                <div className="mt-5 pt-4 border-t border-slate-100 dark:border-slate-700/50 relative z-10">
                  <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-bold ${tax.is_active ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' : 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400'}`}>
                    <span className={`w-1.5 h-1.5 rounded-full ${tax.is_active ? 'bg-green-500' : 'bg-slate-400'}`}></span>
                    {tax.is_active ? 'Active' : 'Inactive'}
                  </span>
                </div>
              </motion.div>
            ))}
          </AnimatePresence>
        </div>
      )}

      {/* Modal */}
      <AnimatePresence>
        {isModalOpen && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/50 backdrop-blur-sm">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="bg-white dark:bg-slate-800 rounded-2xl w-full max-w-md overflow-hidden shadow-2xl border border-slate-200 dark:border-slate-700"
            >
              <div className="flex justify-between items-center p-6 border-b border-slate-100 dark:border-slate-700">
                <h2 className="text-xl font-bold text-slate-900 dark:text-white">
                  {selectedTax ? 'Edit Tax Rate' : 'Add New Tax Rate'}
                </h2>
                <button
                  onClick={() => setIsModalOpen(false)}
                  className="text-slate-400 hover:text-slate-500 dark:hover:text-slate-300 transition"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <form onSubmit={handleSubmit} className="p-6 space-y-4">

                <div>
                  <label className="block text-sm font-bold text-slate-700 dark:text-slate-300 mb-1.5">Total GST Percentage (%) <span className="text-red-500">*</span></label>
                  <div className="relative">
                    <input
                      type="number"
                      step="0.01"
                      required
                      value={formData.gst_percentage}
                      onChange={(e) => setFormData({...formData, gst_percentage: e.target.value})}
                      placeholder="0.00"
                      className="w-full pl-4 pr-10 py-3 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl focus:ring-2 focus:ring-blue-500 focus:outline-none dark:text-white transition-all font-bold"
                    />
                    <Percent className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-[10px] font-bold text-slate-500 dark:text-slate-400 uppercase tracking-widest mb-1.5">CGST (50%)</label>
                    <div className="px-4 py-3 bg-slate-100 dark:bg-slate-900/50 border border-slate-200 dark:border-slate-700 rounded-xl text-slate-500 dark:text-slate-400 font-bold text-sm">
                      {(parseFloat(formData.gst_percentage) / 2 || 0).toFixed(2)}%
                    </div>
                  </div>
                  <div>
                    <label className="block text-[10px] font-bold text-slate-500 dark:text-slate-400 uppercase tracking-widest mb-1.5">SGST (50%)</label>
                    <div className="px-4 py-3 bg-slate-100 dark:bg-slate-900/50 border border-slate-200 dark:border-slate-700 rounded-xl text-slate-500 dark:text-slate-400 font-bold text-sm">
                      {(parseFloat(formData.gst_percentage) / 2 || 0).toFixed(2)}%
                    </div>
                  </div>
                </div>



                <div className="pt-2 flex items-center justify-between">
                  <span className="text-sm font-bold text-slate-700 dark:text-slate-300">Status</span>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input 
                      type="checkbox" 
                      className="sr-only peer"
                      checked={formData.is_active}
                      onChange={(e) => setFormData({...formData, is_active: e.target.checked})}
                    />
                    <div className="w-11 h-6 bg-slate-200 peer-focus:outline-none rounded-full peer dark:bg-slate-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-slate-600 peer-checked:bg-blue-600"></div>
                  </label>
                </div>

                <div className="pt-6 flex gap-3">
                  <button
                    type="button"
                    onClick={() => setIsModalOpen(false)}
                    className="flex-1 px-4 py-3 text-slate-700 dark:text-slate-300 bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 rounded-xl font-bold transition-all"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="flex-1 px-4 py-3 text-white bg-blue-600 hover:bg-blue-700 rounded-xl font-bold transition-all shadow-lg shadow-blue-500/30"
                  >
                    Save Tax Rate
                  </button>
                </div>
              </form>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
}
